import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import GoogleSignInButton from "../components/GoogleSignInButton";
import { Brain, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { warmupBackend } from "../lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/98f7c84d-f342-4447-a832-a43914caf54b/images/46f5eb5d95c612907bdb87dcd40e49acfa3df553832159bb2eca09704dadfdad.png";

export default function Login() {
  const { login, requestMagicLink } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [retryNotice, setRetryNotice] = useState("");
  // iter130 Phase A — detect which auth providers are configured on
  // the server so we only render buttons that actually work.
  const [providers, setProviders] = useState({ google: false, magic_link: true, phone_reset: false });
  const [magicSent, setMagicSent] = useState(false);
  const [magicSubmitting, setMagicSubmitting] = useState(false);

  useEffect(() => {
    warmupBackend();
    api.get("/auth/providers")
      .then(({ data }) => setProviders((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setRetryNotice("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password, {
      onRetry: (attempt, total) => {
        setRetryNotice(`Connection issue — retrying… (${attempt}/${total})`);
      },
    });
    setSubmitting(false);
    setRetryNotice("");
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      nav("/dashboard");
    } else {
      setError(res.error);
    }
  };

  // iter130 Phase A — magic-link request. Uniform response from backend
  // so we always show "check your email" regardless of whether the
  // account exists (anti-enumeration).
  const onSendMagicLink = async () => {
    setError("");
    if (!email.trim()) {
      setError("Enter your email above to send a sign-in link.");
      return;
    }
    setMagicSubmitting(true);
    const res = await requestMagicLink(email.trim().toLowerCase());
    setMagicSubmitting(false);
    if (res.ok) {
      setMagicSent(true);
      toast.success("If that email is registered, a sign-in link is on its way.");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--bt-bg)]">
      {/* Left hero */}
      <div className="relative hidden lg:block">
        <img
          src={HERO_IMG}
          alt="BrainTRADES"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-[var(--bt-bg)]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center">
              <Brain className="w-5 h-5 text-black" strokeWidth={2.5} />
            </span>
            <span className="font-heading text-2xl">
              Brain<span className="text-[var(--bt-accent)]">TRADES</span>
            </span>
          </div>
          <div className="space-y-5 max-w-md">
            <p className="bt-label">Trading Journal · Built for clarity</p>
            <h1 className="font-heading text-5xl leading-[1.05]">
              Master your edge.<br />
              Track every <span className="text-[var(--bt-accent)]">trade.</span>
            </h1>
            <p className="text-[var(--bt-text-muted)] leading-relaxed">
              Log P&L, identify emotional patterns, and see your cumulative growth — across stocks, crypto and forex, in INR or USD.
            </p>
          </div>
          <div className="text-xs text-[var(--bt-text-muted)] font-mono">
            EST. 2026 — TRADER-FIRST · ZERO NOISE
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="w-full max-w-md space-y-7 bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-8"
        >
          <div className="space-y-1.5">
            <p className="bt-label">Sign in</p>
            <h2 className="font-heading text-3xl">Welcome back</h2>
            <p className="text-sm text-[var(--bt-text-muted)]">
              Pick up where you left off.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="bt-label">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-email-input"
                className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="bt-label">Password</Label>
                <Link
                  to={`/auth/forgot${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                  data-testid="login-forgot-link"
                  className="text-xs text-[var(--bt-text-muted)] hover:text-[var(--bt-accent)] underline"
                >
                  Forgot?
                </Link>
              </div>
              <PasswordInput
                id="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                testId="login-password-input"
              />
            </div>
          </div>

          {retryNotice && (
            <div
              data-testid="login-retry-notice"
              className="text-sm text-[var(--bt-warn)] border border-[var(--bt-warn)]/40 bg-[var(--bt-warn)]/10 px-3 py-2 rounded-sm flex items-center gap-2"
            >
              <span className="inline-block w-2 h-2 rounded-full bg-[var(--bt-warn)] animate-pulse" />
              {retryNotice}
            </div>
          )}

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-[var(--bt-danger)] border border-[var(--bt-danger)]/40 bg-[var(--bt-danger)]/10 px-3 py-2 rounded-sm"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold tracking-wide"
          >
            {submitting ? "Signing in…" : (
              <>
                Sign In <ArrowRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>

          {/* iter130 Phase A — alternative sign-in methods. Only
              render buttons for providers that report ready from
              /api/auth/providers, otherwise a user hits a 501. */}
          {(providers.magic_link || providers.google) && (
            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[var(--bt-border)]"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 text-[10px] uppercase tracking-widest text-[var(--bt-text-muted)] bg-[var(--bt-surface)]">
                  Or continue with
                </span>
              </div>
            </div>
          )}

          {providers.magic_link && (
            <Button
              type="button"
              variant="outline"
              onClick={onSendMagicLink}
              disabled={magicSubmitting || magicSent}
              data-testid="login-magic-link-btn"
              className="w-full h-11 rounded-sm border-[var(--bt-border)] hover:bg-[var(--bt-surface-hover)] text-[var(--bt-text)]"
            >
              <Mail className="w-4 h-4 mr-2" />
              {magicSent
                ? "Check your email"
                : (magicSubmitting ? "Sending link…" : "Email me a sign-in link")}
            </Button>
          )}

          {providers.google && (
            /* iter130 Phase A — Google Sign-In button. Renders the
               Google Identity Services button via useEffect. Handler
               lives in <GoogleSignInButton> below. */
            <GoogleSignInButton />
          )}

          <p className="text-sm text-center text-[var(--bt-text-muted)]">
            New here?{" "}
            <Link to="/register" className="text-[var(--bt-accent)] hover:underline" data-testid="link-register">
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
